/**
 * Save Insurance IDs Action
 *
 * Synchronous action that merges insurance creation results (IDs, ehrInsuranceId, etc.)
 * back into the existing insurances context structure without overwriting the full object.
 *
 * This preserves all the insurance verification data (coverageOverview, subscriberInfo, etc.)
 * while adding the newly created insuranceId and EHR IDs.
 */

import { assign } from "xstate";

/**
 * Action metadata
 */
export interface ActionMetadata {
  displayName: string;
  description: string;
  category: "context" | "logging" | "analytics" | "spawn" | "navigation";
}

/**
 * Action with metadata wrapper
 */
export interface ActionWithMetadata {
  impl: ReturnType<typeof assign>;
  metadata: ActionMetadata;
}

/**
 * Insurance creation result from actor
 */
interface InsuranceCreationResult {
  insuranceId: string;
  priority: number;
  ehrInsuranceId?: string;
  ehrInsurancePackageId?: string;
}

/**
 * Actor output structure
 */
interface CreateInsuranceActorOutput {
  insurances: InsuranceCreationResult[];
}

/**
 * Save Insurance IDs Action
 *
 * Merges insurance creation results into context.insurances, preserving all existing data.
 *
 * Before (context.insurances):
 * {
 *   primaryInsurance: { payer: "Aetna", memberId: "123", state: "NY", coverageOverview: {...} },
 *   secondaryInsurance: { payer: "Blue Cross", memberId: "456", state: "CA" }
 * }
 *
 * Actor output:
 * {
 *   insurances: [
 *     { insuranceId: "abc", priority: 1, ehrInsuranceId: "ehr-123" },
 *     { insuranceId: "def", priority: 2, ehrInsuranceId: "ehr-456" }
 *   ]
 * }
 *
 * After (context.insurances):
 * {
 *   primaryInsurance: {
 *     payer: "Aetna",
 *     memberId: "123",
 *     state: "NY",
 *     coverageOverview: {...},
 *     insuranceId: "abc",
 *     ehrInsuranceId: "ehr-123"
 *   },
 *   secondaryInsurance: {
 *     payer: "Blue Cross",
 *     memberId: "456",
 *     state: "CA",
 *     insuranceId: "def",
 *     ehrInsuranceId: "ehr-456"
 *   }
 * }
 */
export const saveInsuranceIds: ActionWithMetadata = {
  // biome-ignore lint/suspicious/noExplicitAny: XState action params are complex
  impl: assign(({ event, context }: any) => {
    const output = event.output as CreateInsuranceActorOutput | undefined;

    if (!output?.insurances || !Array.isArray(output.insurances)) {
      console.warn("[saveInsuranceIds] No insurance results in actor output");
      return {};
    }

    const existingInsurances = context.insurances as {
      primaryInsurance?: Record<string, unknown>;
      secondaryInsurance?: Record<string, unknown>;
    };

    if (!existingInsurances) {
      console.warn("[saveInsuranceIds] No existing insurances in context");
      return {};
    }

    // Build updated insurances object
    const updatedInsurances: {
      primaryInsurance?: Record<string, unknown>;
      secondaryInsurance?: Record<string, unknown>;
    } = {
      ...existingInsurances,
    };

    // Merge IDs into the appropriate insurance objects
    for (const result of output.insurances) {
      if (result.priority === 1 && updatedInsurances.primaryInsurance) {
        updatedInsurances.primaryInsurance = {
          ...updatedInsurances.primaryInsurance,
          insuranceId: result.insuranceId,
          ehrInsuranceId: result.ehrInsuranceId,
          ehrInsurancePackageId: result.ehrInsurancePackageId,
        };
      } else if (
        result.priority === 2 &&
        updatedInsurances.secondaryInsurance
      ) {
        updatedInsurances.secondaryInsurance = {
          ...updatedInsurances.secondaryInsurance,
          insuranceId: result.insuranceId,
          ehrInsuranceId: result.ehrInsuranceId,
          ehrInsurancePackageId: result.ehrInsurancePackageId,
        };
      }
    }

    console.log(
      "[saveInsuranceIds] Merged insurance IDs for priorities:",
      output.insurances.map((r) => r.priority),
    );

    return {
      insurances: updatedInsurances,
    };
  }),
  metadata: {
    displayName: "saveInsuranceIds",
    description:
      "Merge insurance creation results (IDs) into existing insurances context without overwriting",
    category: "context",
  },
};
